import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Save, Users } from "lucide-react";

/**
 * Manager-only — cleanup dialog for the free-text Requester names captured
 * during Take Stock (Reception sign-out). Uses the generic admin_list factory
 * on the backend (`/requesters`) which cascades renames onto
 * `db.movements.requester`. Renaming to an EXISTING requester
 * (case-insensitive) auto-merges — that's how you fix typos like
 * "Saraha" → "Sarah" and combine both onto every past movement.
 *
 * Delete is intentionally NOT surfaced here: the factory blocks it while any
 * movement still references the name (which is nearly always the case), and
 * we don't want to wipe historical audit data.
 */
const ManageRequestersDialog = ({ open, onClose, onChanged }) => {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/requesters");
      setRows(data || []);
    } catch (_) {
      toast.error("Could not load requesters");
    }
  };
  useEffect(() => {
    if (open) {
      setQ("");
      load();
    }
  }, [open]);

  if (!open) return null;

  const save = async (row) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/requesters/${row.id}`, {
        name: row.name,
      });
      // The backend returns the SURVIVOR record. A different id means we
      // merged into an existing requester — surface that to the user.
      if (data?.id && data.id !== row.id) {
        toast.success(`Merged into "${data.name}" — past take-outs retagged`);
      } else {
        toast.success(`Saved — "${data?.name || row.name}"`);
      }
      onChanged?.();
      // Common case: the user opened Manage Requesters to fix one typo.
      // Close the dialog automatically so they see the History page update
      // in real time — much less confusing than staring at a stale list.
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const update = (id, patch) =>
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const filtered = rows.filter((r) =>
    q.trim() ? r.name.toLowerCase().includes(q.trim().toLowerCase()) : true,
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      data-testid="manage-requesters-dialog"
      onClick={onClose}
    >
      <div
        className="nb-card w-[min(640px,94vw)] max-h-[88vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-white px-5 py-4 border-b-2 border-black flex items-center justify-between">
          <div>
            <div className="label-eyebrow">Reception sign-out</div>
            <h3 className="font-display text-2xl flex items-center gap-2">
              <Users size={22} strokeWidth={2.5} /> Manage requesters
            </h3>
          </div>
          <button
            data-testid="manage-requesters-close"
            onClick={onClose}
            className="nb-btn bg-white p-2"
          >
            <X size={16} strokeWidth={2.5} />
          </button>
        </div>

        <div className="p-5 flex flex-col gap-3">
          {rows.length > 6 && (
            <input
              data-testid="requester-search"
              className="nb-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Filter by name…"
            />
          )}

          {rows.length === 0 && (
            <div className="text-sm text-[#607087] italic text-center py-4">
              No requesters yet — they&apos;ll appear here after the first Take Stock
              sign-out.
            </div>
          )}
          {rows.length > 0 && filtered.length === 0 && (
            <div className="text-sm text-[#607087] italic text-center py-4">
              No matches for “{q}”.
            </div>
          )}

          {filtered.map((row) => (
            <div
              key={row.id}
              data-testid={`requester-row-${row.id}`}
              className="border-2 border-black rounded-xl p-3 flex items-center gap-2"
            >
              <input
                data-testid={`requester-name-${row.id}`}
                className="nb-input flex-1"
                value={row.name}
                onChange={(e) => update(row.id, { name: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && save(row)}
              />
              <button
                data-testid={`requester-save-${row.id}`}
                onClick={() => save(row)}
                disabled={busy}
                className="nb-btn nb-btn-primary px-3 py-2 text-sm flex items-center gap-1"
              >
                <Save size={14} strokeWidth={2.5} /> Save
              </button>
            </div>
          ))}

          <div className="label-eyebrow text-[#607087] mt-1">
            Renaming a requester cascades to every past movement. Renaming to a
            name that already exists <b>merges</b> the two — all their past
            take-outs are retagged onto the surviving name. Requesters can&apos;t be
            deleted while they still have history.
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageRequestersDialog;
